import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { ConfirmDelete, DetailRow, PageHeader, Panel, StatusBadge } from '@/components/kit';
import { ConfirmAction } from '@/components/confirm';
import { BackLink, recordGate } from '@/components/record';
import Thread from '@/components/tickets/Thread';
import TicketAdminDialog from '@/components/admin/TicketAdminDialog';
import { PRIORITY_STYLES, TICKET_STATUS_STYLES } from '@/shared/constants';
import { TICKET_FIELDS } from '@/shared/fields';
import { committed, idOf, logActivity, nowIso } from '@/shared/data';
import { formatLocalDate, formatRelativeTime, DATE_FORMATS } from '@/utils/date';

export const route = { path: '/admin/tickets/:id', layout: 'owner', access: "['Admin_c','Staff_c'].includes(profile)" };

export default function AdminTicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);

  const { data: ticket, loading, error, run } = useFetch(
    () => sdk.table('tickets_c').select(TICKET_FIELDS).get(id).then((r) => r.data ?? null),
    [id],
  );

  const back = <BackLink to="/admin/tickets" label="Back to Tickets" />;
  const gate = recordGate({ back, loading, record: ticket, error, run, what: 'Ticket' });
  if (gate) return gate;

  const closed = ticket.status_c === 'Closed';
  const orderId = idOf(ticket.order_c);

  async function setStatus(status, message) {
    const res = await sdk.table('tickets_c').update({ Id: ticket.Id, status_c: status });
    if (!committed(res, message)) return false;
    logActivity('Ticket', `${status} ticket`, ticket.Name, ticket.email_c);
    run();
    return true;
  }

  async function replied() {
    await sdk.table('tickets_c').update({ Id: ticket.Id, status_c: 'Answered', last_reply_at_c: nowIso() });
    run();
  }

  async function remove() {
    const res = await sdk.table('tickets_c').remove(ticket.Id);
    if (!committed(res, 'Ticket deleted')) return false;
    navigate('/admin/tickets');
    return true;
  }

  return (
    <div className="mx-auto max-w-6xl">
      {back}
      <PageHeader
        eyebrow={`${ticket.category_c || 'Support'} · opened ${formatLocalDate(ticket.CreatedOn, DATE_FORMATS.SHORT)}`}
        title={ticket.Name}
        description={ticket.email_c}
        actions={
          <>
            <StatusBadge value={ticket.status_c} styles={TICKET_STATUS_STYLES} />
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
              <ApperIcon name="Pencil" size={16} />
              Edit
            </Button>
            {closed ? (
              <ConfirmAction
                label="Reopen"
                icon="RotateCcw"
                title="Reopen this ticket?"
                description="It goes back in the open queue."
                busyLabel="Reopening…"
                onConfirm={() => setStatus('Open', 'Ticket reopened')}
              />
            ) : (
              <ConfirmAction
                label="Close ticket"
                icon="CircleCheck"
                title="Close this ticket?"
                description="The customer can still reopen it from their account."
                busyLabel="Closing…"
                onConfirm={() => setStatus('Closed', 'Ticket closed')}
              />
            )}
            <ConfirmDelete
              title="Delete this ticket?"
              description="The ticket and its thread disappear for the customer too. This cannot be undone."
              onConfirm={remove}
            />
          </>
        }
      />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <Thread ticketId={ticket.Id} author="Support" closed={closed} onReplied={replied} canManage customerLabel="Customer" />

        <Panel title="Details" icon="Info">
          <dl className="divide-y divide-border">
            <DetailRow label="Status">
              <StatusBadge value={ticket.status_c} styles={TICKET_STATUS_STYLES} />
            </DetailRow>
            <DetailRow label="Priority">
              <StatusBadge value={ticket.priority_c} styles={PRIORITY_STYLES} />
            </DetailRow>
            <DetailRow label="Category">{ticket.category_c}</DetailRow>
            <DetailRow label="Order">
              {orderId ? (
                <Link to={`/admin/orders/${orderId}`} className="font-mono text-primary hover:underline">
                  {ticket.order_c?.Name ?? 'View order'}
                </Link>
              ) : null}
            </DetailRow>
            <DetailRow label="Email">
              {ticket.email_c ? (
                <a href={`mailto:${ticket.email_c}`} className="text-primary hover:underline">
                  {ticket.email_c}
                </a>
              ) : null}
            </DetailRow>
            <DetailRow label="Opened">{formatLocalDate(ticket.CreatedOn, DATE_FORMATS.WITH_TIME)}</DetailRow>
            <DetailRow label="Last reply">
              {ticket.last_reply_at_c ? formatRelativeTime(ticket.last_reply_at_c) : null}
            </DetailRow>
          </dl>
        </Panel>
      </div>

      <TicketAdminDialog open={editOpen} onOpenChange={setEditOpen} ticket={ticket} onSaved={run} />
    </div>
  );
}
